import React, { useState } from "react";
import { Group, Text, Button, Loader } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import Logo from "../../common/Logo/Logo";
import WebsiteGeneratorPublishModal from "./WebsiteGeneratorPublishModal";
import moment from "moment";

const COLOR_BORDER_LIGHT = "#e9ecef";

interface WebsiteGeneratorNavBarProps {
  onCancel: () => void;
  onSave: () => void;
  onPublish: () => void;
  hasCustomDomain?: boolean;
  isPreviewLoading?: boolean;
  isPublished?: boolean;
  lastSavedAt?: string | null;
  onPublishSuccess?: () => void;
}

// Format date for display
const formatLastSaved = (isoTimestamp: string | null | undefined): string => {
  if (!isoTimestamp) return "";
  const date = moment(isoTimestamp);
  if (!date.isValid()) return "";
  return date.format("M/D/YYYY");
};

const WebsiteGeneratorNavBar: React.FC<WebsiteGeneratorNavBarProps> = ({
  onCancel,
  onSave,
  onPublish,
  hasCustomDomain = false,
  isPreviewLoading = false,
  isPublished = false,
  lastSavedAt = null,
  onPublishSuccess,
}) => {
  const navigate = useNavigate();
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);

  const handlePublishClick = () => {
    setIsPublishModalOpen(true);
  };

  const handlePublishModalClose = () => {
    setIsPublishModalOpen(false);
  };

  const handlePublishSuccess = () => {
    // Close publish modal
    setIsPublishModalOpen(false);

    // Notify parent if callback provided
    if (onPublishSuccess) {
      onPublishSuccess();
    }

    // Navigate to settings/website with state to show success modal
    navigate("/admin/settings/website", {
      state: { showPublishSuccess: true },
      replace: true,
    });
  };

  // Build status text
  const formattedDate = formatLastSaved(lastSavedAt);
  const statusText = isPublished ? "Published" : "Not Published";
  const displayText = formattedDate
    ? `Last saved ${formattedDate} • ${statusText}`
    : statusText;

  return (
    <>
      <Group
        justify="space-between"
        p="md"
        style={{ borderBottom: `1px solid ${COLOR_BORDER_LIGHT}` }}
      >
        {/* Left: Pagoda Logo */}
        <Logo variant="horizontal" size="sm" />

        {/* Center: Status */}
        <Group gap="md">
          <Text c="dimmed" size="sm">
            {displayText}
          </Text>
        </Group>

        {/* Right: Action Buttons */}
        <Group gap="sm">
          {/* Loading Spinner */}
          {isPreviewLoading && <Loader size={20} />}
          <Button variant="subtle" color="gray" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="light" color="blue" onClick={onSave}>
            Save
          </Button>
          <Button color="blue" onClick={handlePublishClick}>
            Publish
          </Button>
        </Group>
      </Group>

      {/* Publish Modal */}
      <WebsiteGeneratorPublishModal
        isOpen={isPublishModalOpen}
        onClose={handlePublishModalClose}
        hasCustomDomain={hasCustomDomain}
        onPublishSuccess={handlePublishSuccess}
      />
    </>
  );
};

export default WebsiteGeneratorNavBar;
